/**
 * Printable A4 dashboard report: metrics + 2 compact charts.
 * Rendered for window.print() or "Save as PDF".
 */
import type { AdminAnalyticsData } from '../../api/admin.api';

function formatRub(v: number): string {
  return `${Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₽`;
}

function formatDateLabel(d: string): string {
  const [, m, day] = d.split('-');
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${parseInt(day, 10)} ${months[parseInt(m, 10) - 1] ?? m}`;
}

/** Minimal line chart for print: SVG path + Y labels */
function MiniLineChart({
  data,
  max,
  valueKey,
  formatValue,
  width = 400,
  height = 80,
  ariaTitle,
}: {
  data: { date: string; [k: string]: string | number }[];
  max: number;
  valueKey: string;
  formatValue: (v: number) => string;
  width?: number;
  height?: number;
  ariaTitle: string;
}) {
  if (!data.length) return null;
  const padding = { top: 8, right: 8, bottom: 24, left: 52 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const yScale = max > 0 ? (v: number) => padding.top + innerH - (v / max) * innerH : () => padding.top + innerH / 2;
  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const points = data.map((d, i) => ({
    x: padding.left + i * xStep,
    y: yScale(Number(d[valueKey]) || 0),
    date: d.date,
    value: Number(d[valueKey]) || 0,
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const yTicks = max > 0 ? [0, max / 2, max] : [0];
  const stroke = '#c2185b';
  const gridColor = '#e0e0e0';
  const titleId = `chart-${ariaTitle.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <svg width={width} height={height} style={{ display: 'block' }} role="img" aria-labelledby={titleId}>
      <title id={titleId}>{ariaTitle}</title>
      {yTicks.map((tick, i) => {
        const y = yScale(tick);
        return (
          <g key={i}>
            <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke={gridColor} strokeWidth="1" strokeDasharray="3 2" />
            <text x={padding.left - 4} y={y + 3} textAnchor="end" fontSize="9" fill="#666">
              {formatValue(tick)}
            </text>
          </g>
        );
      })}
      <path d={pathD} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p) => (
        <circle key={p.date} cx={p.x} cy={p.y} r="2" fill={stroke} />
      ))}
      {data.filter((_, i) => i % Math.max(1, Math.floor(data.length / 8)) === 0 || i === data.length - 1).map((d) => {
        const i = data.indexOf(d);
        const x = padding.left + (data.length > 1 ? (i / (data.length - 1)) * innerW : 0);
        return (
          <text key={d.date} x={x} y={height - 6} textAnchor="middle" fontSize="8" fill="#666">
            {formatDateLabel(d.date)}
          </text>
        );
      })}
    </svg>
  );
}

export default function DashboardReportPrint({
  data,
  periodLabel,
}: {
  data: AdminAnalyticsData;
  periodLabel: string;
}) {
  const m = data.metrics;
  const charts = data.charts;
  const revenueByDay = charts?.revenueByDay ?? [];
  const usersGrowth = charts?.usersGrowth ?? [];
  const maxRevenue = Math.max(1, ...revenueByDay.map((d) => Number(d.total) || 0));
  const maxUsers = Math.max(1, ...usersGrowth.map((d) => d.count || 0));
  const periodText = data.periodFrom && data.periodTo
    ? `${periodLabel} (${data.periodFrom} — ${data.periodTo})`
    : periodLabel;

  return (
    <div
      className="dashboard-report-sheet"
      lang="ru"
      style={{
        width: '210mm',
        height: '297mm',
        overflow: 'hidden',
        padding: '12mm 14mm',
        boxSizing: 'border-box',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: '12px',
        color: '#1a1a1a',
        backgroundColor: '#fff',
      }}
    >
      <h1 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600 }}>Отчёт дашборда</h1>
      <p style={{ margin: '0 0 16px 0', fontSize: '11px', color: '#666' }}>Период: {periodText}</p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px 16px',
          marginBottom: '16px',
        }}
      >
        <div style={{ padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Выручка</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatRub(Number(m?.totalRevenue) || 0)}</div>
        </div>
        <div style={{ padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Оплаченных заказов</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Number(m?.orderCount) || 0}</div>
        </div>
        <div style={{ padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Средний чек</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatRub(Number(m?.avgCheck) || 0)}</div>
        </div>
        <div style={{ padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Новые пользователи</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Number(m?.newUsersPeriod) || 0}</div>
        </div>
        <div style={{ padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Активные пользователи</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Math.round(Number(m?.activeClients) ?? 0)}</div>
        </div>
        <div style={{ padding: '10px', border: '1px solid #eee', borderRadius: '8px' }}>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>ARPU</div>
          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatRub(Number(m?.arpu) || 0)}</div>
        </div>
      </div>

      {revenueByDay.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Выручка по дням</div>
          <MiniLineChart
            data={revenueByDay}
            max={maxRevenue}
            valueKey="total"
            formatValue={(v) => formatRub(v)}
            width={400}
            height={80}
            ariaTitle="Выручка по дням"
          />
        </div>
      )}

      {usersGrowth.length > 0 && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Рост пользователей</div>
          <MiniLineChart
            data={usersGrowth}
            max={maxUsers}
            valueKey="count"
            formatValue={(v) => String(Math.round(v))}
            width={400}
            height={80}
            ariaTitle="Рост пользователей по дням"
          />
        </div>
      )}

      <p style={{ marginTop: '16px', fontSize: '10px', color: '#999' }}>
        Сформировано {new Date().toLocaleString('ru-RU')}. Для печати в PDF выберите «Сохранить как PDF» в диалоге печати.
      </p>
    </div>
  );
}
